import { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../lib/auth';
import clsx from 'clsx';
import {
  Page, PageHeader, card, cardBody, CardHeader, input, label, hint,
  btnPrimary, btnSecondary, Alert, Badge,
} from '../components/ui';
import {
  IconUser, IconLock, IconGlobe, IconSettings, IconMessage, IconLink, IconCloud, IconSmartphone,
  IconImage, IconX, IconCopy, IconExternal, IconSpinner,
} from '../components/Icons';

interface PortalSettings {
  heroImageUrl?: string;
  heroStyle?: 'image' | 'gradient' | 'minimal';
  heroGradient?: string;
  heroOverlayOpacity?: number;
  siteName?: string;
  siteNameEn?: string;
  logoUrl?: string;
  primaryColor?: string;
  primaryDark?: string;
  updatedAt?: string;
}

// SHA256 hash function
async function sha256(message: string): Promise<string> {
  const msgBuffer = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

function RadioCard({ checked, onChange, title, description, name }: {
  checked: boolean;
  onChange: () => void;
  title: string;
  description: string;
  name: string;
}) {
  return (
    <label className={clsx(
      'flex items-start gap-3 rounded-lg border px-4 py-3 cursor-pointer transition-colors',
      checked ? 'border-primary bg-primary-light/60' : 'border-line hover:bg-ground',
    )}>
      <input type="radio" name={name} checked={checked} onChange={onChange} className="mt-1 accent-[#2E4467]" />
      <span className="flex flex-col">
        <span className="text-sm font-medium">{title}</span>
        <span className="text-[13px] text-muted">{description}</span>
      </span>
    </label>
  );
}

export default function Settings() {
  const { user, token } = useAuthStore();
  const [apiUrl, setApiUrl] = useState(
    import.meta.env.VITE_API_URL || 'https://group-booking-api.georgec520w.workers.dev/api'
  );
  const [liffId, setLiffId] = useState('2008873533-7E4Unab9');
  const [copied, setCopied] = useState(false);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Portal settings state
  const [portalSettings, setPortalSettings] = useState<PortalSettings>({});
  const [isLoadingPortal, setIsLoadingPortal] = useState(true);
  const [isSavingPortal, setIsSavingPortal] = useState(false);
  const [portalSuccess, setPortalSuccess] = useState('');
  const [portalError, setPortalError] = useState('');
  const [isUploadingHero, setIsUploadingHero] = useState(false);
  const heroInputRef = useRef<HTMLInputElement>(null);

  // Load portal settings
  useEffect(() => {
    loadPortalSettings();
  }, []);

  async function loadPortalSettings() {
    setIsLoadingPortal(true);
    try {
      const response = await fetch(`${apiUrl}/portal/settings`);
      const data = await response.json();
      if (data.success && data.data) {
        setPortalSettings(data.data);
      }
    } catch (err) {
      console.error('Failed to load portal settings:', err);
    } finally {
      setIsLoadingPortal(false);
    }
  }

  async function savePortalSettings() {
    setIsSavingPortal(true);
    setPortalSuccess('');
    setPortalError('');
    try {
      const response = await fetch(`${apiUrl}/portal/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(portalSettings),
      });
      const data = await response.json();
      if (data.success) {
        setPortalSettings(data.data);
        setPortalSuccess('網站設定已儲存。');
      } else {
        setPortalError(data.error || '儲存失敗');
      }
    } catch (err) {
      console.error('Failed to save portal settings:', err);
      setPortalError('網路錯誤，請稍後再試');
    } finally {
      setIsSavingPortal(false);
    }
  }

  async function handleHeroImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setPortalError('請上傳 JPG、PNG 或 WebP 格式的圖片');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setPortalError('圖片大小不能超過 5MB');
      return;
    }

    setIsUploadingHero(true);
    setPortalError('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', 'hero');

      const response = await fetch(`${apiUrl}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();
      if (data.success && data.data?.url) {
        setPortalSettings(prev => ({
          ...prev,
          heroImageUrl: data.data.url,
          heroStyle: 'image',
        }));
        setPortalSuccess('背景圖片已上傳。記得按「儲存設定」。');
      } else {
        setPortalError(data.error || '上傳失敗');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setPortalError('上傳失敗，請稍後再試');
    } finally {
      setIsUploadingHero(false);
      if (heroInputRef.current) {
        heroInputRef.current.value = '';
      }
    }
  }

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('請填寫所有欄位');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('新密碼至少需要 6 個字元');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('新密碼與確認密碼不相符');
      return;
    }

    if (currentPassword === newPassword) {
      setPasswordError('新密碼不能與目前密碼相同');
      return;
    }

    setIsChangingPassword(true);

    try {
      const currentPasswordHash = await sha256(currentPassword);
      const newPasswordHash = await sha256(newPassword);

      const response = await fetch(`${apiUrl}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          currentPasswordHash,
          newPasswordHash,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setPasswordError(data.error || '密碼變更失敗');
        return;
      }

      setPasswordSuccess('密碼已變更。');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      console.error('Change password error:', err);
      setPasswordError('網路錯誤，請稍後再試');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const liffUrl = liffId ? `https://liff.line.me/${liffId}` : '';
  const copyLiffUrl = async () => {
    if (!liffUrl) return;
    await navigator.clipboard.writeText(liffUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const quickLinks = [
    { href: 'https://developers.line.biz/console/', title: 'LINE Developers', desc: '管理 LIFF 和 Messaging API', Icon: IconMessage },
    { href: 'https://dash.cloudflare.com/', title: 'Cloudflare Dashboard', desc: '管理 Workers 和 Pages', Icon: IconCloud },
    { href: 'https://group-booking-api.georgec520w.workers.dev/api/events', title: 'API Events', desc: '查看 API 回應', Icon: IconLink },
    { href: 'https://group-booking-liff.pages.dev/', title: 'LIFF 前台', desc: '報名表單頁面', Icon: IconSmartphone },
  ];

  return (
    <Page narrow>
      <PageHeader title="設定" subtitle="帳號、密碼與前台網站設定" />

      {/* Account */}
      <section className={card}>
        <CardHeader icon={<IconUser size={18} />} title="帳號資訊" />
        <div className={clsx(cardBody, 'flex items-center gap-4')}>
          <div className="w-14 h-14 rounded-full bg-primary text-white flex items-center justify-center text-xl font-bold shrink-0">
            {(user?.displayName?.[0] || user?.username?.[0] || 'A').toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="font-semibold">{user?.displayName || user?.username}</div>
            <div className="text-sm text-muted">@{user?.username}</div>
          </div>
          <Badge tone="primary" className="ml-auto">{user?.role === 'admin' || user?.role === 'super_admin' ? '管理員' : '工作人員'}</Badge>
        </div>
      </section>

      {/* Change Password */}
      <section className={card}>
        <CardHeader icon={<IconLock size={18} />} title="變更密碼" description="至少 6 個字元。變更後其他裝置需重新登入。" />
        <form onSubmit={handleChangePassword} className={clsx(cardBody, 'flex flex-col gap-5')}>
          {passwordError && <Alert tone="error">{passwordError}</Alert>}
          {passwordSuccess && <Alert tone="success">{passwordSuccess}</Alert>}

          <div>
            <label htmlFor="currentPassword" className={label}>目前密碼</label>
            <input
              id="currentPassword"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className={input}
              placeholder="請輸入目前密碼"
              autoComplete="current-password"
            />
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="newPassword" className={label}>新密碼</label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className={input}
                placeholder="至少 6 個字元"
                autoComplete="new-password"
              />
            </div>
            <div>
              <label htmlFor="confirmPassword" className={label}>確認新密碼</label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                aria-invalid={!!confirmPassword && newPassword !== confirmPassword}
                className={clsx(input, confirmPassword && newPassword !== confirmPassword && 'border-bad focus-visible:border-bad focus-visible:ring-bad/20')}
                placeholder="再次輸入新密碼"
                autoComplete="new-password"
              />
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="text-[13px] text-bad mt-1.5">密碼不相符</p>
              )}
            </div>
          </div>

          <div className="flex justify-end pt-1">
            <button
              type="submit"
              disabled={isChangingPassword || !currentPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword}
              className={btnPrimary}
            >
              {isChangingPassword && <IconSpinner size={16} />}
              {isChangingPassword ? '變更中' : '變更密碼'}
            </button>
          </div>
        </form>
      </section>

      {/* Portal Settings (Site Settings) */}
      <section className={card}>
        <CardHeader icon={<IconGlobe size={18} />} title="網站設定" description="影響報名者在 LINE 上看到的首頁。" />
        <div className={clsx(cardBody, 'flex flex-col gap-5')}>
          {portalError && <Alert tone="error">{portalError}</Alert>}
          {portalSuccess && <Alert tone="success">{portalSuccess}</Alert>}

          {isLoadingPortal ? (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted">
              <IconSpinner size={18} />
              載入中
            </div>
          ) : (
            <>
              <div>
                <label htmlFor="siteName" className={label}>網站名稱</label>
                <input
                  id="siteName"
                  type="text"
                  value={portalSettings.siteName || ''}
                  onChange={(e) => setPortalSettings(prev => ({ ...prev, siteName: e.target.value }))}
                  className={input}
                  placeholder="喵喵團報"
                />
              </div>

              <div>
                <span className={label}>首頁背景樣式</span>
                <div className="grid gap-3 sm:grid-cols-2">
                  <RadioCard
                    name="heroStyle"
                    checked={portalSettings.heroStyle !== 'image'}
                    onChange={() => setPortalSettings(prev => ({ ...prev, heroStyle: 'gradient' }))}
                    title="漸層背景"
                    description="使用主題顏色自動產生"
                  />
                  <RadioCard
                    name="heroStyle"
                    checked={portalSettings.heroStyle === 'image'}
                    onChange={() => setPortalSettings(prev => ({ ...prev, heroStyle: 'image' }))}
                    title="背景圖片"
                    description="上傳自己的橫幅圖片"
                  />
                </div>
              </div>

              {portalSettings.heroStyle === 'image' && (
                <div>
                  <label className={label}>首頁背景圖片 <span className="text-muted font-normal">建議 1200 × 400</span></label>
                  {portalSettings.heroImageUrl ? (
                    <div className="relative">
                      <img
                        src={portalSettings.heroImageUrl}
                        alt="首頁背景預覽"
                        className="w-full h-40 object-cover rounded-lg border border-line"
                      />
                      <button
                        type="button"
                        onClick={() => setPortalSettings(prev => ({ ...prev, heroImageUrl: undefined }))}
                        aria-label="移除背景圖片"
                        className="absolute top-2 right-2 h-8 w-8 inline-flex items-center justify-center rounded-md bg-white/95 border border-line text-muted hover:text-bad hover:bg-bad-soft"
                      >
                        <IconX size={16} />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => heroInputRef.current?.click()}
                      disabled={isUploadingHero}
                      className="w-full rounded-lg border-2 border-dashed border-line-strong p-8 text-center hover:border-primary hover:bg-primary-light/40 transition-colors disabled:opacity-60"
                    >
                      {isUploadingHero ? (
                        <div className="flex flex-col items-center gap-2 text-sm text-muted">
                          <IconSpinner size={22} />
                          上傳中
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2">
                          <IconImage size={26} className="text-[#9AA3B0]" />
                          <div className="text-sm font-medium">點擊上傳背景圖片</div>
                          <div className="text-[13px] text-muted">JPG、PNG 或 WebP，最大 5MB</div>
                        </div>
                      )}
                    </button>
                  )}
                  <input
                    ref={heroInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleHeroImageUpload}
                    className="hidden"
                  />
                </div>
              )}

              {portalSettings.heroStyle === 'image' && portalSettings.heroImageUrl && (
                <div>
                  <label htmlFor="overlay" className={label}>
                    遮罩深度 <span className="tabular text-muted font-normal">{Math.round((portalSettings.heroOverlayOpacity ?? 0.4) * 100)}%</span>
                  </label>
                  <input
                    id="overlay"
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={(portalSettings.heroOverlayOpacity ?? 0.4) * 100}
                    onChange={(e) => setPortalSettings(prev => ({ ...prev, heroOverlayOpacity: parseInt(e.target.value) / 100 }))}
                    className="w-full h-2 bg-line-soft rounded-lg appearance-none cursor-pointer accent-[#2E4467]"
                  />
                  <div className="flex justify-between text-xs text-muted mt-1">
                    <span>透明</span>
                    <span>深色</span>
                  </div>
                </div>
              )}

              <div className="flex justify-end pt-4 border-t border-line-soft">
                <button type="button" onClick={savePortalSettings} disabled={isSavingPortal} className={btnPrimary}>
                  {isSavingPortal && <IconSpinner size={16} />}
                  {isSavingPortal ? '儲存中' : '儲存設定'}
                </button>
              </div>
            </>
          )}
        </div>
      </section>

      {/* LINE LIFF */}
      <section className={card}>
        <CardHeader icon={<IconMessage size={18} />} title="LINE LIFF" description="報名表單在 LINE 內開啟的位址。" />
        <div className={clsx(cardBody, 'flex flex-col gap-5')}>
          <div>
            <label htmlFor="liffId" className={label}>LIFF ID</label>
            <input
              id="liffId"
              type="text"
              value={liffId}
              onChange={(e) => setLiffId(e.target.value)}
              className={clsx(input, 'font-mono')}
              placeholder="1234567890-xxxxxxxx"
            />
          </div>
          <div>
            <label htmlFor="liffUrl" className={label}>LIFF 表單網址</label>
            <div className="flex gap-2">
              <input id="liffUrl" type="text" value={liffUrl} readOnly className={clsx(input, 'font-mono')} />
              <button type="button" onClick={copyLiffUrl} disabled={!liffUrl} className={clsx(btnSecondary, 'shrink-0')}>
                <IconCopy size={16} />
                {copied ? '已複製' : '複製'}
              </button>
            </div>
            <p className={hint}>把這個網址放到 LINE 官方帳號的圖文選單或訊息中。</p>
          </div>
        </div>
      </section>

      {/* API */}
      <section className={card}>
        <CardHeader icon={<IconSettings size={18} />} title="API 連線" />
        <div className={cardBody}>
          <label htmlFor="apiUrl" className={label}>API 網址</label>
          <input
            id="apiUrl"
            type="text"
            value={apiUrl}
            onChange={(e) => setApiUrl(e.target.value)}
            className={clsx(input, 'font-mono')}
            readOnly
          />
          <p className={hint}>由部署環境變數設定，此處僅供查看。</p>
        </div>
      </section>

      {/* Quick Links */}
      <section className={card}>
        <CardHeader icon={<IconLink size={18} />} title="快速連結" />
        <div className={clsx(cardBody, 'grid gap-3 sm:grid-cols-2')}>
          {quickLinks.map(({ href, title, desc, Icon }) => (
            <a
              key={href}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 rounded-lg border border-line px-4 py-3 hover:bg-ground transition-colors"
            >
              <span className="h-9 w-9 rounded-md bg-ground text-muted flex items-center justify-center shrink-0 group-hover:bg-white">
                <Icon size={18} />
              </span>
              <span className="flex flex-col min-w-0">
                <span className="text-sm font-medium">{title}</span>
                <span className="text-[13px] text-muted">{desc}</span>
              </span>
              <IconExternal size={14} className="ml-auto text-[#9AA3B0] group-hover:text-muted shrink-0" />
            </a>
          ))}
        </div>
      </section>
    </Page>
  );
}
